/**
 * VeyopItemRenderer for arc-curved text.
 *
 * Renders each character at explicit (x,y) on an arc and rotates it to match the
 * tangent of the arc.
 *
 * textCurve         — radius in pixels (positive integer, 0 = off)
 * textCurveReverse  — false = arch up, true = arch down
 * textCurveSpacing  — extra spacing added per character (px)
 */
import { measureArcTextLayout } from './ArcLayout';

interface DesignItem {
  id: string;
  type: string;
  text?: string;
  width?: unknown;
  height?: unknown;
  fontFamily?: unknown;
  fontSize?: unknown;
  fontWeight?: unknown;
  fontStyle?: unknown;
  color?: unknown;
  letterSpacing?: unknown;
  lineHeight?: unknown;
  opacity?: unknown;
  textCurve?: unknown;
  textCurveReverse?: unknown;
  textCurveSpacing?: unknown;
  textCurvePreview?: unknown;
  [key: string]: unknown;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function s(v: unknown, fallback: string): string {
  const x = String(v ?? '').trim();
  return x ? x : fallback;
}

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K): SVGElementTagNameMap[K] {
  return document.createElementNS(SVG_NS, tag);
}

export function renderArcText(item: DesignItem, container: HTMLElement): () => void {
  const layout = measureArcTextLayout(item);
  if (!layout) return () => {};

  const fill = s(item.color, '#000');
  const opacity = Number.isFinite(Number(item.opacity)) ? Number(item.opacity) : 1;

  const svg = svgEl('svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('viewBox', `0 0 ${layout.width} ${layout.height}`);
  svg.style.overflow = 'visible';
  svg.style.position = 'absolute';
  svg.style.left = '0';
  svg.style.top = '0';
  // Important: allow pointer events so clicking glyphs selects the item.
  // We do not attach listeners here; events should bubble to the host canvas.
  svg.style.pointerEvents = 'auto';

  const group = svgEl('g');
  group.setAttribute('fill', fill);
  group.setAttribute('opacity', String(opacity));
  group.setAttribute('font-family', layout.fontFamily);
  group.setAttribute('font-size', String(layout.fontSize));
  group.setAttribute('font-weight', layout.fontWeight);
  group.setAttribute('font-style', layout.fontStyle);
  group.style.pointerEvents = 'visiblePainted';
  svg.appendChild(group);

  if (item.textCurvePreview && layout.guidePath) {
    const guide = svgEl('path');
    guide.setAttribute('d', layout.guidePath);
    guide.setAttribute('fill', 'none');
    guide.setAttribute('stroke', '#38bdf8');
    guide.setAttribute('stroke-width', String(Math.max(1, layout.fontSize * 0.04)));
    guide.setAttribute('stroke-dasharray', '6 6');
    guide.setAttribute('stroke-linecap', 'round');
    guide.setAttribute('opacity', '0.55');
    guide.style.pointerEvents = 'none';
    svg.appendChild(guide);
  }

  layout.glyphs.forEach((glyph) => {
    const t = svgEl('text');
    t.setAttribute('x', String(glyph.x));
    t.setAttribute('y', String(glyph.y));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('dominant-baseline', 'middle');
    t.setAttribute('transform', `rotate(${glyph.rotation} ${glyph.x} ${glyph.y})`);
    t.style.pointerEvents = 'visiblePainted';
    t.textContent = glyph.ch;

    group.appendChild(t);
  });

  container.style.position = container.style.position || 'relative';
  container.style.overflow = 'visible';
  container.appendChild(svg);

  return () => {
    if (svg.parentNode === container) {
      container.removeChild(svg);
    }
  };
}
